import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
// Preview-only redesigned landing (Feb 2026). Mounted at /landing-v2 for PK
// side-by-side comparison against the live Landing.jsx. Not linked from anywhere
// in the app until an explicit swap is approved.
const LandingV2 = lazy(() => import("@/pages/LandingV2"));
// NEET explainer guide — public, exam-neutral platform push (Feb 2026).
const NeetGuide = lazy(() => import("@/pages/NeetGuide"));
// CET instant free-preview mirror of /neet/instant (Feb 2026, SPEC-06).
const CetInstantPredictor = lazy(() => import("@/pages/CetInstantPredictor"));
// Heavy authenticated student pages — lazy-loaded to shave ~3500 lines off
// the initial JS bundle. First-time visitors landing on `/` don't need any
// of this code (Dashboard, Predictions, Compare, etc. are behind auth), so
// deferring the download is a pure win for landing-page LCP.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const NewProfile = lazy(() => import("@/pages/NewProfile"));
const InstantPredictor = lazy(() => import("@/pages/InstantPredictor"));
const Predictions = lazy(() => import("@/pages/Predictions"));
// NEET-specific viewer for /neet/predictions/:id — CET's Predictions.jsx
// renders percentile-space fields (branch_name / closing_used / delta)
// which don't exist on NEET rank-space rows and produce blank names + NaN.
const NeetPredictions = lazy(() => import("@/pages/NeetPredictions"));
// Curated shortlist view — /neet/shortlist/:profileId
const NeetShortlist = lazy(() => import("@/pages/NeetShortlist"));
const Compare = lazy(() => import("@/pages/Compare"));
const Step2B = lazy(() => import("@/pages/Step2B"));
const Step3 = lazy(() => import("@/pages/Step3"));
const TopColleges = lazy(() => import("@/pages/TopColleges"));
const EngineeringBranches = lazy(() => import("@/pages/EngineeringBranches"));
// Feb 2026 (landing revamp): CAP Rounds Info as its own public page, wired
// to the Engineering sub-menu in the top nav Streams dropdown.
const CapRounds = lazy(() => import("@/pages/CapRounds"));
const Shortlist = lazy(() => import("@/pages/Shortlist"));
const Advisor = lazy(() => import("@/pages/Advisor"));
const AdvisorRound1Page = lazy(() => import("@/pages/AdvisorRound1Page"));
const AdvisorR2FormPage = lazy(() => import("@/pages/AdvisorR2FormPage"));
// Legal pages are static, text-heavy and rarely visited — lazy-load so they
// don't inflate the main bundle (also improves Lighthouse TTI for the
// homepage which is the biggest source of organic search traffic).
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Refund = lazy(() => import("@/pages/Refund"));
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PortalLogin, { redirectForRole } from "@/pages/portal/PortalLogin";
import PortalForgotPassword from "@/pages/portal/PortalForgotPassword";
import PortalResetPassword from "@/pages/portal/PortalResetPassword";
import PartnerSignup from "@/pages/portal/PartnerSignup";
// Portal admin + partner pages — lazy-loaded. Fewer than 5% of visitors ever
// reach these; keeping them in the main bundle only made landing-page LCP
// worse for the 95% who are students. All 20 chunks together weigh ~40-70 kB
// gzipped and stream in on-demand.
const AdminDashboard = lazy(() => import("@/pages/portal/AdminDashboard"));
const AdminProfiles = lazy(() => import("@/pages/portal/AdminProfiles"));
const AdminUsers = lazy(() => import("@/pages/portal/AdminUsers"));
const AdminSignups = lazy(() => import("@/pages/portal/AdminSignups"));
const AdminPartnerAttribution = lazy(() => import("@/pages/portal/AdminPartnerAttribution"));
const AdminAuditLog = lazy(() => import("@/pages/portal/AdminAuditLog"));
const AdminLaunchChecklist = lazy(() => import("@/pages/portal/AdminLaunchChecklist"));
const AdminPartnerApps = lazy(() => import("@/pages/portal/AdminPartnerApps"));
const AdminPartners = lazy(() => import("@/pages/portal/AdminPartners"));
const AdminPayouts = lazy(() => import("@/pages/portal/AdminPayouts"));
const PartnerDashboard = lazy(() => import("@/pages/portal/PartnerDashboard"));
const PartnerProfiles = lazy(() => import("@/pages/portal/PartnerProfiles"));
const PartnerStudents = lazy(() => import("@/pages/portal/PartnerStudents"));
const PartnerKyc = lazy(() => import("@/pages/portal/PartnerKyc"));
const PartnerPayouts = lazy(() => import("@/pages/portal/PartnerPayouts"));
const PartnerCreateStudent = lazy(() => import("@/pages/portal/PartnerCreateStudent"));
const PartnerTickets = lazy(() => import("@/pages/portal/PartnerTickets"));
const AdminTickets = lazy(() => import("@/pages/portal/AdminTickets"));
const AdminRounds = lazy(() => import("@/pages/portal/AdminRounds"));
const AdminSmsDelivery = lazy(() => import("@/pages/portal/AdminSmsDelivery"));
const AdminDataAudit = lazy(() => import("@/pages/portal/AdminDataAudit"));
const AdminVisitors = lazy(() => import("@/pages/portal/AdminVisitors"));
const AdminCutoffs = lazy(() => import("@/pages/portal/AdminCutoffs"));
const AdminRoundLaunch = lazy(() => import("@/pages/portal/AdminRoundLaunch"));
const AdminCampaignLinks = lazy(() => import("@/pages/portal/AdminCampaignLinks"));
const AdminNurture = lazy(() => import("@/pages/portal/AdminNurture"));
const AdminCrm = lazy(() => import("@/pages/portal/AdminCrm"));
const AdminAnalytics = lazy(() => import("@/pages/portal/AdminAnalytics"));
const AdminReceipts = lazy(() => import("@/pages/portal/AdminReceipts"));
const AdminAbandonedLeads = lazy(() => import("@/pages/portal/AdminAbandonedLeads"));
const AdminCreateProfile = lazy(() => import("@/pages/portal/AdminCreateProfile"));
const AnalystCrm = lazy(() => import("@/pages/portal/AnalystCrm"));
const RevealPanel = lazy(() => import("@/pages/RevealPanel"));
const VideoLibrary = lazy(() => import("@/pages/VideoLibrary"));
import Partners from "@/pages/Partners";
import VisitorTracker from "@/components/VisitorTracker";
import PostGoogleConsentGate from "@/components/PostGoogleConsentGate";
import DisclaimerModal from "@/components/DisclaimerModal";
import PriceDropTicker from "@/components/PriceDropTicker";
import { getCurrentPortalUser } from "@/lib/portalApi";

// Maps a portal role to the right landing inside the portal so a user who
// somehow lands on a consumer route gets sent home, not into a 404.
const PORTAL_HOME = {
  partner: "/portal/partner",
  admin: "/portal/admin",
  analyst: "/portal/analyst",
};

function PrivateRoute({ children }) {
  const { session, loading, internalRole, internalRoleChecked } = useAuth();
  if (loading || (session && !internalRoleChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }
  if (!session) return <Navigate to="/" replace />;
  // Portal account using a consumer route → bounce to their portal. Partners
  // must NEVER see /dashboard, /predictions, /top-colleges, etc.
  if (internalRole && PORTAL_HOME[internalRole]) {
    return <Navigate to={PORTAL_HOME[internalRole]} replace />;
  }
  return children;
}

function PublicOnlyRoute({ children }) {
  const { session, loading, internalRole, internalRoleChecked } = useAuth();
  if (loading) return null;
  // Session just came in but internal-role check is still in flight. Normally
  // we render null (so a portal user doesn't briefly see the consumer UI). BUT
  // when a fresh signup is mid-OTP, unmounting Landing here wipes AuthModal's
  // local state → the OTP step never paints even though the pending flag
  // correctly holds the /dashboard redirect. Keep Landing mounted in that case.
  if (session && !internalRoleChecked) {
    try {
      if (sessionStorage.getItem("dhruva_pending_signup_otp") === "1") {
        return children;
      }
    } catch (_) { /* sessionStorage may be blocked */ }
    return null;
  }
  if (session) {
    if (internalRole && PORTAL_HOME[internalRole]) {
      return <Navigate to={PORTAL_HOME[internalRole]} replace />;
    }
    // Signup mobile-OTP gate: when SignupFlow has just called supabase.signUp
    // it sets `dhruva_pending_signup_otp` in sessionStorage BEFORE the auth-
    // state-change fires. We hold the redirect until the OTP step completes
    // (or the user backs out) so the in-modal '6-digit code' screen can
    // actually paint. Without this, Supabase's auto-session would immediately
    // yank the user to /dashboard and the OTP step would never be seen.
    try {
      if (sessionStorage.getItem("dhruva_pending_signup_otp") === "1") {
        return children;
      }
    } catch (_) { /* sessionStorage may be blocked in strict privacy modes */ }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PortalRoute({ children, roles }) {
  const u = getCurrentPortalUser();
  if (!u) return <Navigate to="/portal/login" replace />;
  if (roles && !roles.includes(u.role)) return <Navigate to={redirectForRole(u.role)} replace />;
  // Every portal page is now lazy-loaded (see App.js top). Wrapping the auth
  // gate itself in Suspense means we don't have to touch each individual
  // <Route> declaration — the fallback shows for any of the 20 portal chunks
  // while it streams in on first visit.
  return <Suspense fallback={<PortalFallback />}>{children}</Suspense>;
}

function PortalFallback() {
  return (
    <div
      className="min-h-screen bg-slate-900 text-slate-300 flex items-center justify-center p-6"
      data-testid="portal-fallback"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="w-9 h-9 rounded-full border-2 border-slate-700 border-t-amber-500 animate-spin" />
        <div className="text-[12.5px] text-slate-400 tracking-wide">Loading admin console…</div>
      </div>
    </div>
  );
}

function LegalFallback() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="text-slate-400 text-sm animate-pulse">Loading…</div>
    </div>
  );
}

/**
 * StudentAppFallback — shown while a code-split student page (Dashboard,
 * Predictions, Compare, ...) is being downloaded. Deliberately minimal so
 * it renders instantly and doesn't add its own JS to the critical path.
 * The subtle amber accent matches the AdmissionCrafts brand palette so it
 * doesn't feel like a blank white page for the fraction of a second before
 * the real chunk streams in.
 */
function StudentAppFallback() {
  return (
    <div
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6"
      data-testid="student-app-fallback"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-amber-600 animate-spin" />
        <div className="text-[13px] text-slate-500 font-medium tracking-wide">Loading your predictions…</div>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <VisitorTracker />
          {/* Global price-drop ticker — sits above every route including
              Landing, Reveal Panel, Dashboard etc. Session-dismissible. */}
          <PriceDropTicker />
          {/* Global contact-completeness gate — mounted here (above <Routes />)
              so that ANY route the user visits while authenticated enforces
              the mobile-capture loop, not just AppLayout-wrapped pages.
              /reveal/:profileId is a public route without AppLayout, so
              hoisting the gate to the router level guarantees coverage. The
              gate self-suppresses when there is no Supabase session, so it's
              inert for anonymous visitors on Landing / Terms / Privacy.
              DisclaimerModal is hoisted alongside it because the gate waits
              for the disclaimer ack; leaving DisclaimerModal inside AppLayout
              only would strand /reveal-first users behind a suppressed gate. */}
          <DisclaimerModal />
          <PostGoogleConsentGate />
          <Routes>
            {/* Public / Student app */}
            <Route path="/" element={<PublicOnlyRoute><Landing /></PublicOnlyRoute>} />
            {/* Preview-only redesigned landing — Feb 2026. Public, side-by-side
                comparison route. Live Landing.jsx above is unchanged until PK
                approves the swap. Not indexed / not linked from nav. */}
            <Route path="/landing-v2" element={<Suspense fallback={<LegalFallback />}><LandingV2 /></Suspense>} />
            {/* Public NEET explainer guide — SPEC-06 (Feb 2026). Reachable
                from LandingV2 Guides section and from CTAs on the NEET
                predictor page. */}
            <Route path="/guides/neet" element={<Suspense fallback={<LegalFallback />}><NeetGuide /></Suspense>} />
            {/* SPEC-06 (Feb 2026): CET single-screen free preview at parity
                with /neet/instant. Public, no auth, no persistence. */}
            <Route path="/cet/instant" element={<Suspense fallback={<LegalFallback />}><CetInstantPredictor /></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={<LegalFallback />}><Terms /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<LegalFallback />}><Privacy /></Suspense>} />
            <Route path="/refund" element={<Suspense fallback={<LegalFallback />}><Refund /></Suspense>} />
            <Route path="/help/videos" element={<Suspense fallback={<LegalFallback />}><VideoLibrary /></Suspense>} />
            {/* SPEC-04 · NEET instant predictor. Public, no auth. */}
            <Route path="/neet/instant" element={<Suspense fallback={<LegalFallback />}><InstantPredictor /></Suspense>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Dashboard /></Suspense></PrivateRoute>} />
            <Route path="/new-profile" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><NewProfile /></Suspense></PrivateRoute>} />
            <Route path="/predictions/:id" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Predictions /></Suspense></PrivateRoute>} />
            <Route path="/neet/predictions/:id" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><NeetPredictions /></Suspense></PrivateRoute>} />
            <Route path="/neet/shortlist/:profileId" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><NeetShortlist /></Suspense></PrivateRoute>} />
            {/* Task 3: /reveal is publicly reachable — resume-token flow needs
                to work for parents who aren't signed in. Auth is enforced
                inside RevealPanel via the /reveal endpoint. */}
            <Route path="/reveal/:profileId" element={<Suspense fallback={<StudentAppFallback />}><RevealPanel /></Suspense>} />
            <Route path="/compare/:profileId" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Compare /></Suspense></PrivateRoute>} />
            <Route path="/top-colleges" element={<Suspense fallback={<StudentAppFallback />}><TopColleges /></Suspense>} />
            <Route path="/cap-rounds" element={<Suspense fallback={<StudentAppFallback />}><CapRounds /></Suspense>} />
            <Route path="/step2b/:profileId" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Step2B /></Suspense></PrivateRoute>} />
            <Route path="/step3/:profileId" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Step3 /></Suspense></PrivateRoute>} />
            <Route path="/engineering-branches" element={<Suspense fallback={<StudentAppFallback />}><EngineeringBranches /></Suspense>} />
            <Route path="/shortlist" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Shortlist /></Suspense></PrivateRoute>} />
            <Route path="/advisor" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><Advisor /></Suspense></PrivateRoute>} />
            <Route path="/advisor/round-1" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><AdvisorRound1Page /></Suspense></PrivateRoute>} />
            <Route path="/advisor/r2-form/:profileId" element={<PrivateRoute><Suspense fallback={<StudentAppFallback />}><AdvisorR2FormPage /></Suspense></PrivateRoute>} />

            {/* Marketing alias — `/predict` points to the public landing (where pricing & paid CTA live). */}
            <Route path="/predict" element={<Navigate to="/" replace />} />

            {/* Public partner application */}
            <Route path="/partners" element={<Partners />} />
            <Route path="/partner-signup" element={<PartnerSignup />} />
            {/* Legacy /partner/apply route — redirect to new direct signup */}
            <Route path="/partner/apply" element={<Navigate to="/partner-signup" replace />} />

            {/* Portal */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
            <Route path="/portal/reset-password" element={<PortalResetPassword />} />
            <Route path="/portal/admin" element={<PortalRoute roles={["admin"]}><AdminDashboard /></PortalRoute>} />
            <Route path="/portal/admin/profiles" element={<PortalRoute roles={["admin"]}><AdminProfiles /></PortalRoute>} />
            <Route path="/portal/admin/signups" element={<PortalRoute roles={["admin"]}><AdminSignups /></PortalRoute>} />
            <Route path="/portal/admin/users" element={<PortalRoute roles={["admin"]}><AdminUsers /></PortalRoute>} />
            <Route path="/portal/admin/partner-apps" element={<PortalRoute roles={["admin"]}><AdminPartnerApps /></PortalRoute>} />
            <Route path="/portal/admin/partners" element={<PortalRoute roles={["admin"]}><AdminPartners /></PortalRoute>} />
            <Route path="/portal/admin/tickets" element={<PortalRoute roles={["admin"]}><AdminTickets /></PortalRoute>} />
            <Route path="/portal/admin/rounds" element={<PortalRoute roles={["admin"]}><AdminRounds /></PortalRoute>} />
            <Route path="/portal/admin/sms" element={<PortalRoute roles={["admin"]}><AdminSmsDelivery /></PortalRoute>} />
            <Route path="/portal/admin/data-audit" element={<PortalRoute roles={["admin"]}><AdminDataAudit /></PortalRoute>} />
            <Route path="/portal/admin/cutoffs" element={<PortalRoute roles={["admin"]}><AdminCutoffs /></PortalRoute>} />
            <Route path="/portal/admin/round-launch" element={<PortalRoute roles={["admin"]}><AdminRoundLaunch /></PortalRoute>} />
            <Route path="/portal/admin/visitors" element={<PortalRoute roles={["admin"]}><AdminVisitors /></PortalRoute>} />
            <Route path="/portal/admin/campaign-links" element={<PortalRoute roles={["admin"]}><AdminCampaignLinks /></PortalRoute>} />
            <Route path="/portal/admin/nurture" element={<PortalRoute roles={["admin"]}><AdminNurture /></PortalRoute>} />
            <Route path="/portal/admin/crm" element={<PortalRoute roles={["admin"]}><AdminCrm /></PortalRoute>} />
            <Route path="/portal/admin/analytics" element={<PortalRoute roles={["admin"]}><AdminAnalytics /></PortalRoute>} />
            <Route path="/portal/admin/receipts" element={<PortalRoute roles={["admin"]}><AdminReceipts /></PortalRoute>} />
            <Route path="/portal/admin/abandoned-leads" element={<PortalRoute roles={["admin"]}><AdminAbandonedLeads /></PortalRoute>} />
            <Route path="/portal/admin/create-profile" element={<PortalRoute roles={["admin"]}><AdminCreateProfile /></PortalRoute>} />
            <Route path="/portal/admin/payouts" element={<PortalRoute roles={["admin"]}><AdminPayouts /></PortalRoute>} />
            <Route path="/portal/admin/attribution" element={<PortalRoute roles={["admin"]}><AdminPartnerAttribution /></PortalRoute>} />
            <Route path="/portal/admin/audit-log" element={<PortalRoute roles={["admin"]}><AdminAuditLog /></PortalRoute>} />
            <Route path="/portal/admin/launch-checklist" element={<PortalRoute roles={["admin"]}><AdminLaunchChecklist /></PortalRoute>} />

            <Route path="/portal/analyst" element={<PortalRoute roles={["analyst"]}><AdminProfiles analystMode /></PortalRoute>} />
            <Route path="/portal/analyst/crm" element={<PortalRoute roles={["analyst", "admin"]}><AnalystCrm /></PortalRoute>} />

            <Route path="/portal/partner" element={<PortalRoute roles={["partner"]}><PartnerDashboard /></PortalRoute>} />
            <Route path="/portal/partner/profiles" element={<PortalRoute roles={["partner"]}><PartnerProfiles /></PortalRoute>} />
            <Route path="/portal/partner/students" element={<PortalRoute roles={["partner"]}><PartnerStudents /></PortalRoute>} />
            <Route path="/portal/partner/payouts" element={<PortalRoute roles={["partner"]}><PartnerPayouts /></PortalRoute>} />
            <Route path="/portal/partner/tickets" element={<PortalRoute roles={["partner"]}><PartnerTickets /></PortalRoute>} />
            <Route path="/portal/partner/new" element={<PortalRoute roles={["partner"]}><PartnerCreateStudent /></PortalRoute>} />
            <Route path="/portal/partner/kyc" element={<PortalRoute roles={["partner"]}><PartnerKyc /></PortalRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
